<script lang="ts" setup>
import type { ResponseError } from '#strict-fetch';

const TEST_ID = 'section-subscribe';

const { t } = useI18n();
const modalStore = useModalStore();
const dataLayer = useDataLayer();

const email = ref('');
const { execute, isLoading } = useRequest(CommonAPI.subscribe, () => ({
  body: { email: email.value },
}));

const onSubmit = async () => {
  execute()
    ?.then(() => {
      dataLayer.append({ event: 'email-subscribe' }, ['user']);
      modalStore.showSuccess({
        title: t('message.success'),
        action: t('message.return'),
      });
      email.value = '';
    })
    .catch((e: ResponseError) => {
      modalStore.showError({
        title: getFetchErrorMessage(e, t('message.error')),
        action: t('message.return'),
      });
    });
};
</script>

<template>
  <section class="sectionSubscribe" :data-test-id="TEST_ID">
    <div class="sectionSubscribe__inner">
      <div class="sectionSubscribe__left">
        <div class="sectionSubscribe__titleWrap">
          <p class="sectionSubscribe__titleMain">{{ t('title_main') }}</p>
          <p class="sectionSubscribe__titleDiscount">{{ t('title_discount') }}</p>
        </div>
      </div>

      <div class="sectionSubscribe__right">
        <form @submit.prevent="onSubmit">
          <label class="sectionSubscribe__field">
            <input
              v-model="email"
              autocomplete="email"
              inputmode="email"
              name="email"
              placeholder="email@example.ru"
              class="sectionSubscribe__input"
              :data-test-id="`${TEST_ID}-email-input`"
            />
            <span class="sectionSubscribe__hint">
              {{ t('hint_after.before') }}
              <NuxtLink to="/personal" class="sectionSubscribe__hintLink">
                {{ t('hint_after.consent_link') }}
              </NuxtLink>
              {{ t('hint_after.middle') }}
              <NuxtLink to="/policy" class="sectionSubscribe__hintLink">
                {{ t('hint_after.policy_link') }}
              </NuxtLink>
              {{ t('hint_after.after') }}
            </span>
          </label>

          <BaseButton
            outlined
            :loading="isLoading"
            mobile-size="xs"
            type="submit"
            class="sectionSubscribe__submit"
            :data-test-id="`${TEST_ID}-submit`"
          >
            {{ t('action') }}
          </BaseButton>
        </form>
      </div>
    </div>
  </section>
</template>

<i18n>
ru:
  title_main: "Пробовать первыми и превращать\nуход в ритуал.\nПодписывайтесь на рассылку."
  title_discount: "Скидка 15% — для вдохновения."
  hint_after:
    before: 'Нажимая «Подписаться», вы соглашаетесь с обработкой персональных данных и получением рассылки согласно'
    consent_link: ''
    middle: ''
    policy_link: 'политике конфиденциальности'
    after: '.'
  image_alt: Подпишитесь на рассылку от Don't Touch My Skin
  action: Подписаться
  message:
    success: Спасибо, что вы с нами! Остался последний шаг — проверьте почту и подтвердите подписку.
    error: Что-то пошло не так. Пожалуйста, напишите нам на info{'@'}dtmskin.com
    return: Вернуться

en:
  title: |
    Try first and turn
    care into a ritual.
    Subscribe to our newsletter.
    15% discount — for inspiration.
  hint_after:
    before: 'By clicking "Subscribe", you agree to the processing of personal data and receiving newsletters according to our'
    consent_link: ''
    middle: ''
    policy_link: 'privacy policy'
    after: '.'
  image_alt: Subscribe to the Don't Touch My Skin newsletter
  action: Subscribe
  message:
    success: Thank you for being with us! Just one last step — please check your inbox and confirm your subscription.
    error: Something went wrong. Please email us at info{'@'}dtmskin.com
    return: Return
</i18n>

<style lang="scss">
.sectionSubscribe {
  background-color: #c8adcd;
  border-radius: 16px;
  padding: 60px 40px;
  margin: 20px 0;
  height: 370px;

  @include mq('sm') {
    padding: 20px 10px;
    border-radius: 12px;
    height: 100%;
  }

  &__inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 40px;
    height: 100%;

    @include mq('sm') {
      flex-direction: column;
      align-items: flex-start;
      gap: 32px;
    }
  }

  &__left {
    flex: 0 0 auto;
    max-width: 45%;
    align-self: flex-start; // прижать к верху

    @include mq('sm') {
      max-width: 100%;
      align-self: auto;
    }
  }

  &__right {
    display: flex;
    flex: 0 0 auto;
    width: 45%;
    align-items: center;

    @include mq('sm') {
      width: 100%;
    }
  }

  &__titleMain {
    font-size: 32px;
    line-height: 36px;
    color: #ffffff;
    margin: 0 0 16px 0;

    @include mq('sm') {
      font-size: 24px;
      line-height: 32px;
      margin-bottom: 20px;
    }
  }

  &__titleDiscount {
    font-size: 32px;
    line-height: 36px;
    color: #ffffff;
    margin: 0;

    @include mq('sm') {
      font-size: 24px;
      line-height: 32px;
    }
  }

  &__field {
    display: block;
  }

  &__input {
    width: 100%;
    display: block;
    border: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.7);
    background: none;
    font-size: 24px;
    line-height: 24px;
    color: #fff;
    outline: none;
    padding: 8px 0;

    &::placeholder {
      color: rgba(255, 255, 255, 0.6);
    }

    @include mq('sm') {
      font-size: 18px;
    }
  }

  &__hint {
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.1px;
    color: rgba(255, 255, 255, 0.85);
    display: block;
    margin-top: 12px;
    margin-bottom: 28px;
  }

  @include mq('sm') {
    font-size: 10px;
  }

  &__hintLink {
    color: rgba(255, 255, 255, 0.85);
    text-decoration: underline;

    &:hover {
      color: #fff;
    }
  }

  &__submit {
    width: 50%;
    border-radius: 50px;
    border-color: #fff;
    color: #fff;

    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }

    @include mq('sm') {
      width: 100%;
    }
  }
}
</style>
